import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  auth, 
  db, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  doc, 
  getDoc, 
  setDoc 
} from '../firebase';

interface AdminLoginProps {
  onLogin: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (!username || !password) {
      alert('아이디와 비밀번호를 모두 입력해 주세요.');
      return;
    }

    setIsLoading(true);

    // Convert username to email format for Firebase Auth if needed (e.g. makerstory or seven_iris -> seven_iris@naver.com)
    let usernameForEmail = username;
    if (!username.includes('@')) {
      if (username === 'makerstory' || username === 'seven_iris') {
        usernameForEmail = 'seven_iris@naver.com';
      } else {
        usernameForEmail = `${username}@makerstory.com`;
      }
    }

    try {
      // 1. Try to sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, usernameForEmail, password);
      const uid = userCredential.user.uid;

      // 2. Fetch or create Firestore user profile ensuring isAdmin is true
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists() || !userDoc.data()?.isAdmin) {
        await setDoc(userDocRef, {
          id: uid,
          name: "관리자",
          email: usernameForEmail,
          isAdmin: true,
          points: 99999999,
          joinedAt: new Date().toISOString()
        }, { merge: true });
      }

      onLogin();
      navigate('/admin/dashboard');
    } catch (authError: any) {
      console.warn("Firebase Auth sign-in failed, attempting credentials check...", authError);

      // 3. Fallback: Check if the entered credentials match the master admin configuration
      const CORRECT_ID = process.env.ADMIN_USERNAME || 'makerstory';
      const CORRECT_PW = process.env.ADMIN_PASSWORD || 'qwe123!!';

      const isMasterAdminId = username === CORRECT_ID || username === 'seven_iris' || username === 'seven_iris@naver.com';
      const isCorrectPassword = password === CORRECT_PW;

      if (isMasterAdminId && isCorrectPassword) {
        try {
          // Create the admin account in Firebase Auth
          const userCredential = await createUserWithEmailAndPassword(auth, 'seven_iris@naver.com', password);
          const uid = userCredential.user.uid;

          // Create the corresponding user document in Firestore with isAdmin: true
          await setDoc(doc(db, "users", uid), {
            id: uid,
            name: "관리자",
            email: "seven_iris@naver.com",
            isAdmin: true,
            points: 99999999,
            joinedAt: new Date().toISOString()
          });

          onLogin();
          navigate('/admin/dashboard');
        } catch (createError: any) {
          console.error("Failed to auto-create admin user in Auth/Firestore:", createError);
          alert(`관리자 계정 생성 중 오류가 발생했습니다.\n원인: ${createError.message || createError}`);
        }
      } else {
        alert('아이디 또는 비밀번호가 잘못되었습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white p-8 rounded-2xl shadow-lg max-w-sm w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mx-auto mb-4">
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-6 h-6">
               <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
             </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">관리자 로그인</h2>
          <p className="text-sm text-gray-500 mt-2">상품 및 재고 관리를 위해 로그인하세요.</p>
        </div>
        
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">아이디</label>
            <input 
              type="text" 
              value={username} 
              onChange={(e) => setUsername(e.target.value)} 
              disabled={isLoading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:outline-none disabled:opacity-50" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호</label>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              disabled={isLoading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:outline-none disabled:opacity-50" 
            />
          </div>
          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full py-3 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 transition disabled:bg-gray-400 flex items-center justify-center"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              '로그인'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;